import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowRightStartOnRectangleIcon,
  ArrowsRightLeftIcon,
  CheckCircleIcon,
  DocumentMagnifyingGlassIcon,
  EllipsisHorizontalIcon,
  FolderArrowDownIcon,
  ScissorsIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { PinIcon } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from '@/components/ui/catalyst/dropdown';
import {
  pinDocument,
  selectIsAtSoftLimit,
  selectPinnedDocs,
} from '@/services/slices/pinnedDocsSlice';
import { Blob } from '@/services/types/blob-types';

import { useDocumentTree } from '../context/DocumentTreeContext';

type BulkActionBarProps = {
  onBulkMove?: () => void;
  onBulkDelete?: () => void;
  onBulkDownload?: () => void;
  onBulkRegenerate?: () => void;
  onBulkExtractCoverage?: () => void;
  onBulkResort?: () => void;
  onExtractPages?: () => void;
  onBulkApproveSync?: () => void;
  onTransferToCompanions?: () => void;
  hasCompanions?: boolean;
  /** All blobs for pinning - will be filtered by selection */
  blobs?: Blob[];
  /** Matter ID for pinning functionality */
  matterId?: string;
  /** Matter name for pinning functionality */
  matterName?: string;
  /** Whether the re-sort operation is loading */
  isResortLoading?: boolean;
  onBulkDeleteFolders?: () => void;
};

export const BulkActionBar = ({
  onBulkMove,
  onBulkDelete,
  onBulkDownload,
  onBulkRegenerate,
  onBulkExtractCoverage,
  onBulkResort,
  onExtractPages,
  onBulkApproveSync,
  onTransferToCompanions,
  hasCompanions = false,
  blobs,
  matterId,
  matterName,
  isResortLoading = false,
  onBulkDeleteFolders,
}: BulkActionBarProps) => {
  const dispatch = useDispatch();
  const pinnedDocs = useSelector(selectPinnedDocs);
  const isAtSoftLimit = useSelector(selectIsAtSoftLimit);

  const {
    selectedCount,
    clearSelection,
    selectedFileIds,
    selectedFolderCount,
    clearFolderSelection,
  } = useDocumentTree();
  const hasFileSelection = selectedCount > 0;
  const hasFolderSelection = selectedFolderCount > 0;
  const hasSelection = hasFileSelection || hasFolderSelection;

  // Get selected blobs that are PDFs and not already pinned
  const selectedBlobs = blobs?.filter((blob) => selectedFileIds.has(blob.id));
  const pinnablePdfs =
    selectedBlobs?.filter(
      (blob) =>
        blob.file_mime_type === 'application/pdf' &&
        !pinnedDocs.some((doc) => doc.id === blob.id),
    ) || [];

  // Check if exactly one PDF is selected (for page extraction)
  const selectedPdfs =
    selectedBlobs?.filter(
      (blob) => blob.file_mime_type === 'application/pdf',
    ) || [];
  const hasSinglePdfSelected = selectedPdfs.length === 1;

  const handleClearAll = () => {
    clearSelection();
    clearFolderSelection();
  };

  const handlePinAllPdfs = () => {
    if (!matterId || !matterName) {
      toast.error('Unable to pin: Matter context not available');
      return;
    }

    if (pinnablePdfs.length === 0) {
      toast.info('No new PDFs to pin', {
        description: 'Selected PDFs are already pinned or no PDFs selected',
      });
      return;
    }

    pinnablePdfs.forEach((blob) => {
      dispatch(
        pinDocument({
          id: blob.id,
          matterId,
          matterName,
          fileName: blob.name,
          fileType: blob.file_mime_type || 'application/pdf',
          downloadUrl: blob.download_url,
          pinnedAt: new Date().toISOString(),
        }),
      );
    });

    toast.success(
      `Pinned ${pinnablePdfs.length} PDF${pinnablePdfs.length > 1 ? 's' : ''} to dock`,
    );

    if (isAtSoftLimit) {
      toast.info('Consider unpinning some documents', {
        description: 'You have many documents pinned',
      });
    }
  };

  // Calculate the visibility class based on selection
  const visibilityClass = hasSelection
    ? 'opacity-100 translate-y-0'
    : 'opacity-0 translate-y-1 pointer-events-none';

  return (
    <div
      className={`ml-auto transition-all duration-200 flex items-center space-x-1 p-2 ${visibilityClass}`}
    >
      <div className="text-[10px] font-medium text-muted-foreground mr-1">
        {hasFileSelection && hasFolderSelection
          ? `${selectedCount} ${selectedCount === 1 ? 'file' : 'files'}, ${selectedFolderCount} ${selectedFolderCount === 1 ? 'folder' : 'folders'}`
          : hasFolderSelection
            ? `${selectedFolderCount} ${selectedFolderCount === 1 ? 'folder' : 'folders'}`
            : `${selectedCount} ${selectedCount === 1 ? 'file' : 'files'}`}{' '}
        selected
      </div>

      <TooltipProvider>
        {/* Pin All PDFs button - only show if there are pinnable PDFs */}
        {pinnablePdfs.length > 0 && matterId && matterName && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                plain
                onClick={handlePinAllPdfs}
                disabled={!hasSelection}
                className="text-violet-600 hover:text-violet-700 hover:bg-violet-50 px-0"
              >
                <span className="sr-only">Pin PDFs to dock</span>
                <PinIcon className="h-4 w-4 text-gray-500 mx-0" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Pin {pinnablePdfs.length} PDF
              {pinnablePdfs.length !== 1 ? 's' : ''} to dock
            </TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button plain onClick={onBulkMove} disabled={!hasSelection}>
              <span className="sr-only">Move files</span>
              <ArrowRightStartOnRectangleIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Move selected files</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button plain onClick={onBulkDownload} disabled={!hasSelection}>
              <span className="sr-only">Download files</span>
              <ArrowDownTrayIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Download selected files</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              plain
              className="hover:text-destructive"
              onClick={() => {
                if (hasFolderSelection && onBulkDeleteFolders) {
                  onBulkDeleteFolders();
                }
                if (hasFileSelection && onBulkDelete) {
                  onBulkDelete();
                }
              }}
              disabled={!hasSelection}
            >
              <span className="sr-only">Delete selected</span>
              <TrashIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {hasFolderSelection && hasFileSelection
              ? 'Delete selected files and folders'
              : hasFolderSelection
                ? 'Delete selected folders'
                : 'Delete selected files'}
          </TooltipContent>
        </Tooltip>

        {/* Extract Pages button - only show for single PDF */}
        {hasSinglePdfSelected && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button plain onClick={onExtractPages}>
                <span className="sr-only">Extract pages</span>
                <ScissorsIcon />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Extract pages from PDF</TooltipContent>
          </Tooltip>
        )}

        {/* More actions dropdown */}
        <Dropdown>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownButton
                as={Button}
                plain
                disabled={!hasSelection}
                className="p-1.5"
              >
                <span className="sr-only">More actions</span>
                <EllipsisHorizontalIcon />
              </DropdownButton>
            </TooltipTrigger>
            <TooltipContent>More actions</TooltipContent>
          </Tooltip>
          <DropdownMenu>
            <DropdownItem onClick={onBulkRegenerate} disabled={!hasSelection}>
              <ArrowPathIcon />
              Regenerate Digests
            </DropdownItem>
            <DropdownItem
              onClick={onBulkExtractCoverage}
              disabled={!hasSelection}
            >
              <DocumentMagnifyingGlassIcon />
              Extract Coverage
            </DropdownItem>
            <DropdownItem
              onClick={onBulkResort}
              disabled={!hasSelection || isResortLoading}
            >
              <FolderArrowDownIcon />
              {isResortLoading ? 'Re-Sorting...' : 'Re-Sort Files'}
            </DropdownItem>
            {onBulkApproveSync && (
              <DropdownItem
                onClick={onBulkApproveSync}
                disabled={!hasSelection}
              >
                <CheckCircleIcon />
                Approve Doc Sync
              </DropdownItem>
            )}
            {hasCompanions && (
              <DropdownItem
                onClick={onTransferToCompanions}
                disabled={!hasSelection}
              >
                <ArrowsRightLeftIcon />
                Transfer to Companions
              </DropdownItem>
            )}
          </DropdownMenu>
        </Dropdown>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button plain onClick={handleClearAll} disabled={!hasSelection}>
              <span className="sr-only">Clear selection</span>
              <XMarkIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Clear selection</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export default BulkActionBar;
